import { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  Star,
  MousePointerClick,
  FileText,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Tag,
  Search,
} from 'lucide-react';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
  { label: 'Projects', icon: Building2, path: '/admin/projects' },
  { label: 'Testimonials', icon: Star, path: '/admin/testimonials' },
  { label: 'Enquiries', icon: MessageSquare, path: '/admin/enquiries' },
  { label: 'CTA Analytics', icon: MousePointerClick, path: '/admin/cta' },
  { label: 'Content Manager', icon: FileText, path: '/admin/content' },
  { label: 'Filter Tags', icon: Tag, path: '/admin/filters' },
];

export default function AdminLayout() {
  const { admin, logout, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#47ace2]/40 border-t-[#47ace2] rounded-full animate-spin" />
      </div>
    );
  }

  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`px-5 py-5 border-b border-gray-200 flex items-center gap-3 ${sidebarOpen ? '' : 'justify-center'}`}>
        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
          <img src="/logo.png" alt="Modern Arch Logo" className="w-full h-full object-contain" />
        </div>
        {sidebarOpen && (
          <div>
            <span className="text-slate-800 font-bold text-sm font-outfit block leading-tight">Modern Arch</span>
            <span className="text-slate-400 text-[10px] font-manrope">Admin Panel</span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {navItems.map(({ label, icon: Icon, path }) => (
          <NavLink
            key={path}
            to={path}
            onClick={() => setMobileSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl font-manrope text-sm font-medium transition-all duration-200
              ${isActive
                ? 'bg-[#47ace2] text-white shadow-md shadow-[#47ace2]/25'
                : 'text-slate-500 hover:bg-[#47ace2]/8 hover:text-[#47ace2]'
              } ${!sidebarOpen ? 'justify-center' : ''}`
            }
            title={!sidebarOpen ? label : undefined}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-4 border-t border-gray-200 space-y-1">
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-500 hover:bg-red-50 hover:text-red-500 font-manrope text-sm font-medium transition-all ${!sidebarOpen ? 'justify-center' : ''}`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && <span>Logout</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 shadow-sm ${
          sidebarOpen ? 'w-60' : 'w-[72px]'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setMobileSidebarOpen(false)} />
          <aside className="relative w-64 bg-white border-r border-gray-200 flex flex-col z-10 shadow-xl">
            <button
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              onClick={() => setMobileSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-6 h-16 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            {/* Mobile menu toggle */}
            <button
              className="lg:hidden text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            {/* Desktop sidebar toggle */}
            <button
              className="hidden lg:block text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => setSidebarOpen(v => !v)}
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Search bar */}
            <div className="hidden md:flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2 w-56">
              <Search className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-sm font-manrope">Search...</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <button className="relative p-2 rounded-xl bg-gray-100 text-slate-400 hover:text-slate-600 hover:bg-gray-200 transition-colors">
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#47ace2] rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(v => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <div className="w-7 h-7 rounded-lg bg-[#47ace2] flex items-center justify-center text-white text-xs font-bold font-outfit shadow-sm">
                  A
                </div>
                <span className="text-slate-600 text-sm font-manrope hidden sm:block font-medium">{admin.name}</span>
                <ChevronDown className="w-3 h-3 text-slate-400 hidden sm:block" />
              </button>
              {userMenuOpen && (
                <div className="absolute right-0 top-12 w-44 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-slate-700 text-sm font-manrope font-semibold">{admin.name}</p>
                    <p className="text-slate-400 text-xs font-manrope capitalize">{admin.role}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-3 text-red-500 hover:bg-red-50 text-sm font-manrope transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
